/**
 * The opening.
 *
 * A brand moment that runs **once per genuine launch** — a cold page load, or
 * opening the installed PWA. Not on route changes, not on returning from a
 * modal, and emphatically not on `visibilitychange`, which fires every time
 * Safari is backgrounded and would turn switching apps into a title sequence.
 *
 * Three things it must never do, in order of how badly they hurt:
 *
 *   **Trap anyone.** It is `inert` and `aria-hidden` for its whole life. There is
 *   nothing to focus inside it, so there is nothing to trap; a keyboard user is
 *   already on Home behind it, which is rendering the entire time.
 *
 *   **Block the app.** Home is mounted underneath from the first frame. This is
 *   a layer over a working app, not a gate in front of a loading one, so session
 *   restoration, storage and the first interaction are never waiting on it.
 *
 *   **Fail to leave.** It removes itself on a timer that does not depend on
 *   anything else succeeding. If session initialisation throws, if the network
 *   is dead, if `animationend` never fires — the overlay still goes.
 */

import { useEffect, useRef, useState } from 'react';
import { BrandMark } from './BrandMark';

/**
 * Total time on screen, inside the 900-1400ms the checkpoint asks for.
 *
 * Long enough to read as deliberate, short enough that a learner opening the app
 * to write something down is not made to wait for a logo.
 */
export const LAUNCH_MS = 1150;
/** Reduced motion gets a brand frame, not a sequence. */
export const LAUNCH_REDUCED_MS = 420;

/**
 * Set on the document as soon as the first launch has been decided.
 *
 * A module-level flag would be reset by React's development double-mount and by
 * hot reloading, and both would replay the animation. The document survives
 * both, and is discarded by the only thing that should replay it: a real
 * navigation.
 */
const MARKER = 'data-mm-launched';

export function hasLaunchedThisDocument(): boolean {
  return typeof document !== 'undefined' && document.documentElement.hasAttribute(MARKER);
}

/**
 * Is this a genuine launch?
 *
 * `back_forward` is the interesting exclusion: restoring a page from Safari's
 * back/forward cache re-runs nothing, but if it did, the learner would see the
 * opening again for pressing Back.
 */
export function isGenuineLaunch(): boolean {
  if (typeof window === 'undefined' || typeof document === 'undefined') return false;
  if (hasLaunchedThisDocument()) return false;

  const [entry] = performance.getEntriesByType?.('navigation') ?? [];
  const type = (entry as PerformanceNavigationTiming | undefined)?.type;
  if (type === 'back_forward') return false;
  return true;
}

export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export interface LaunchOverlayProps {
  /** Test seam. Left alone, it decides for itself. */
  force?: boolean;
  onDone?: () => void;
}

export function LaunchOverlay({ force, onDone }: LaunchOverlayProps) {
  const [visible, setVisible] = useState(() => force ?? isGenuineLaunch());
  const doneRef = useRef(onDone);
  doneRef.current = onDone;

  useEffect(() => {
    if (!visible) return;
    document.documentElement.setAttribute(MARKER, '');

    const still = prefersReducedMotion();
    // One timer, and it owns the whole lifetime. Hanging removal off
    // `animationend` would mean a dropped frame, a paused tab or a browser that
    // never fires it leaves a learner staring at a logo with no way past.
    const timer = window.setTimeout(
      () => {
        setVisible(false);
        doneRef.current?.();
      },
      still ? LAUNCH_REDUCED_MS : LAUNCH_MS,
    );

    return () => window.clearTimeout(timer);
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      className={`mm-launch ${prefersReducedMotion() ? 'mm-launch--still' : ''}`}
      // Nothing here is interactive and nothing here is content: the app behind
      // it is already the page. Announcing a decorative overlay would put a
      // screen-reader user in a room with no exits.
      aria-hidden="true"
      // React 18's typings predate `inert`, and the attribute has to be a real
      // attribute rather than a prop for the browser to honour it.
      {...{ inert: '' }}
      data-testid="launch-overlay"
    >
      <div className="mm-launch__mark">
        <BrandMark size={92} />
      </div>
      <p className="mm-launch__word">MusicMentor</p>
      <p className="mm-launch__line">Write. See. Hear.</p>
    </div>
  );
}
